import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  HStack, 
  Text, 
  IconButton,
  Badge,
  Table,
} from '@chakra-ui/react';
import { FiPlus, FiCopy, FiTrash2 } from 'react-icons/fi';
import { nip19 } from 'nostr-tools';
import NewIdentityModal from '../components/NewIdentityModal';
import { toaster } from '../components/ui/toaster';
import { PageWrapper } from '../components/PageWrapper';
import { EmptyState } from '../components/EmptyState';

interface Identity {
  id: string;
  source: 'generated' | 'nsec' | 'signer';
  publicKey: string;
  npub: string;
  nsec?: string;
}

function IdentitiesPage() {
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('nostr-identities');
    if (stored) {
      setIdentities(JSON.parse(stored));
    }
  }, []);

  const saveIdentities = (newIdentities: Identity[]) => {
    setIdentities(newIdentities);
    localStorage.setItem('nostr-identities', JSON.stringify(newIdentities));
  };

  const handleSaveIdentity = (identityData: {
    pubkey: string;
    nsec?: string;
    source: 'generated' | 'nsec' | 'signer';
  }) => {
    const npub = nip19.npubEncode(identityData.pubkey);
    
    const newIdentity: Identity = {
      id: crypto.randomUUID(),
      source: identityData.source,
      publicKey: identityData.pubkey,
      npub,
      nsec: identityData.nsec,
    };

    saveIdentities([...identities, newIdentity]);
  };

  const deleteIdentity = (id: string) => {
    const filtered = identities.filter(i => i.id !== id);
    saveIdentities(filtered);
    toaster.create({
      title: 'Identity deleted',
      type: 'info',
      duration: 2000,
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toaster.create({
      title: `${label} copied`,
      type: 'info',
      duration: 1500,
    });
  };

  return (
    <>
      <PageWrapper>
        <HStack justify="space-between">
          <Text fontSize="2xl" fontWeight="bold">Identities</Text>
          <Button
            colorPalette="blue"
            onClick={() => setShowModal(true)}
          >
            <FiPlus /> Create New
          </Button>
        </HStack>

            <Box overflowX="auto">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Source</Table.ColumnHeader>
                    <Table.ColumnHeader>Public Key (npub)</Table.ColumnHeader>
                    <Table.ColumnHeader>Private Key</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {identities.map((identity) => (
                    <Table.Row key={identity.id}>
                      <Table.Cell>
                        <Badge
                          colorPalette={
                            identity.source === 'generated' ? 'green' : 
                            identity.source === 'nsec' ? 'blue' : 'purple'
                          }
                        >
                          {identity.source}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack>
                          <Text fontSize="sm" fontFamily="mono" noOfLines={1} maxW="300px">
                            {identity.npub}
                          </Text>
                          <IconButton
                            aria-label="Copy npub"
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(identity.npub, 'npub')}
                          >
                            <FiCopy />
                          </IconButton>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>
                        {identity.nsec ? (
                          <HStack>
                            <Text fontSize="sm">nsec</Text>
                            <IconButton
                              aria-label="Copy nsec"
                              size="sm"
                              variant="ghost"
                              colorPalette="orange"
                              onClick={() => copyToClipboard(identity.nsec!, 'nsec')}
                            >
                              <FiCopy />
                            </IconButton>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">Not available</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <IconButton
                          aria-label="Delete identity"
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => deleteIdentity(identity.id)}
                        >
                          <FiTrash2 />
                        </IconButton>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                  {identities.length === 0 && (
                    <EmptyState
                      colSpan={4}
                      message="No identities yet. Create your first identity to get started."
                    />
                  )}
                </Table.Body>
              </Table.Root>
            </Box>
      </PageWrapper>
      
      <NewIdentityModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveIdentity}
      />
    </>
  );
}

export default IdentitiesPage;